import { Pencil, TrashIcon } from "lucide-react";
import { Badge, Button } from "@capacit-com/lara-law-shared-ui";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { CustomColumnDef } from "@/components/data-table/types/custom-column-def";
import { ConsolidationPriority } from "@/models/consolidation";
import {
  MODE_LABELS,
  getPriorityStatusDisplay,
} from "../consolidationPriorityDisplay";

const formatDate = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime())
    ? ""
    : parsed.toLocaleDateString("da-DK");
};

/**
 * Columns for the priorities table — the tone-of-voice settings table's shape:
 * sortable headers via DataTableColumnHeader, row click edits, and an actions
 * column. Sorting/search happen client-side in usePriorityTableData; the
 * accessorKeys double as its sorting ids.
 */
export const PriorityColumns = (
  handleDeleteDialogOpen: (priority: ConsolidationPriority) => void,
  handleEditDialogOpen: (priority: ConsolidationPriority) => void
): CustomColumnDef<ConsolidationPriority>[] => [
  {
    accessorKey: "lawTitle",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Lov" />
    ),
    cell: ({ row }: { row: { original: ConsolidationPriority } }) => {
      const priority = row.original;
      const title = priority.lawTitle ?? priority.lawExternalId;
      return (
        <div className="flex w-full min-w-0 flex-col" title={title}>
          <span className="truncate font-medium text-foreground">{title}</span>
          <span className="truncate text-xs text-muted-foreground">
            {priority.pivotExternalId}
          </span>
        </div>
      );
    },
    meta: {
      displayName: "Lov",
      dataType: "text",
      // The flexing column: absorbs the free width and truncates long law
      // titles (full title on hover) so the table never scrolls sideways.
      cellClassName: "w-full max-w-0",
    },
  },
  {
    accessorKey: "mode",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Omfang" />
    ),
    cell: ({ row }: { row: { original: ConsolidationPriority } }) =>
      MODE_LABELS[row.original.mode],
    meta: {
      displayName: "Omfang",
      dataType: "text",
    },
  },
  {
    accessorKey: "level",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Prioritet" />
    ),
    cell: ({ row }: { row: { original: ConsolidationPriority } }) => (
      <span title="1 er højeste prioritet">{row.original.level}</span>
    ),
    meta: {
      displayName: "Prioritet",
      dataType: "number",
    },
  },
  {
    accessorKey: "requestedByName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Oprettet af" />
    ),
    cell: ({ row }: { row: { original: ConsolidationPriority } }) =>
      row.original.requestedByName ?? "—",
    meta: {
      displayName: "Oprettet af",
      dataType: "text",
    },
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Dato" />
    ),
    cell: ({ row }: { row: { original: ConsolidationPriority } }) =>
      formatDate(row.original.createdAt),
    meta: {
      displayName: "Dato",
      dataType: "date",
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }: { row: { original: ConsolidationPriority } }) => {
      // undefined = the page's status batch is still loading; null = loaded,
      // no rollup (no queue rows yet).
      if (row.original.status === undefined) {
        return (
          <span className="inline-block h-5 w-24 animate-pulse rounded-md bg-muted" />
        );
      }
      const status = getPriorityStatusDisplay(row.original.status);
      return (
        <div className="flex max-w-48 flex-col gap-0.5">
          <Badge variant={status.variant} className="w-fit">
            {status.label}
          </Badge>
          {status.detail && (
            <span className="text-xs text-muted-foreground">
              {status.detail}
            </span>
          )}
        </div>
      );
    },
    enableSorting: false,
    meta: {
      displayName: "Status",
    },
  },
  {
    accessorKey: "actions",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Handlinger" />
    ),
    cell: ({ row }: { row: { original: ConsolidationPriority } }) => {
      const priority = row.original;
      const name = priority.lawTitle ?? priority.pivotExternalId;
      return (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Rediger prioritering af ${name}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleEditDialogOpen(priority);
            }}
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Fjern prioritering af ${name}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleDeleteDialogOpen(priority);
            }}
          >
            <TrashIcon className="size-4 text-destructive" />
          </Button>
        </div>
      );
    },
    enableSorting: false,
    meta: {
      displayName: "Handlinger",
    },
  },
];
